
import { useEffect, useState } from 'react';
import { useTheme as useContextTheme } from '@/contexts/theme/ThemeContext';

export function useTheme() {
  const [theme, setTheme] = useState<'light' | 'dark'>('light'); // Changed default to light
  const contextTheme = useContextTheme();
  
  useEffect(() => {
    // Initial theme check
    const checkTheme = () => {
      const isDark = document.documentElement.classList.contains('dark');
      setTheme(isDark ? 'dark' : 'light');
    };
    
    // Check theme on mount
    checkTheme();
    
    // Set up observer for theme changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkTheme();
        }
      });
    });
    
    observer.observe(document.documentElement, { attributes: true });
    
    return () => observer.disconnect();
  }, []);
  
  const setThemeValue = (newTheme: 'light' | 'dark') => {
    const root = document.documentElement;
    
    // Remove both classes and add the new one
    root.classList.remove('dark', 'light');
    root.classList.add(newTheme);
    
    // Save theme preference
    localStorage.setItem('theme', newTheme);
    
    // Apply theme-specific colors from our context
    if (!contextTheme.loading) {
      Object.entries(contextTheme.colors[newTheme]).forEach(([key, value]) => {
        root.style.setProperty(`--${key}`, value);
      });
    }
    
    // Update state
    setTheme(newTheme);
  };
  
  return { 
    theme, 
    setTheme: setThemeValue,
    ...contextTheme 
  };
}

export function useThemeInitialization() {
  const { colors, loading } = useContextTheme();
  
  useEffect(() => {
    // Get theme from localStorage or use light as default (changed from dark)
    const savedTheme = localStorage.getItem('theme');
    const defaultTheme = 'light'; // Changed from dark to light
    const theme = savedTheme || defaultTheme;
    
    // Apply theme
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(theme);
    
    // Apply saved colors if available
    if (!loading) {
      const root = document.documentElement;
      Object.entries(colors[theme as 'light' | 'dark']).forEach(([key, value]) => {
        root.style.setProperty(`--${key}`, value);
      });
    }
  }, [colors, loading]);
}
